import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/d97xwjyk_Logo.png';

export default function DemoTransition({ active, onComplete }) {
  const [phase, setPhase] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const runningRef = useRef(false);

  useEffect(() => {
    if (!active || runningRef.current) { if (!active) { setPhase(0); runningRef.current = false; } return; }
    runningRef.current = true;
    setPhase(1);
    const t1 = setTimeout(() => setPhase(2), 800);
    const t2 = setTimeout(() => setPhase(3), 2200);
    const t3 = setTimeout(() => setPhase(4), 3400);
    const t4 = setTimeout(() => { setPhase(0); runningRef.current = false; onCompleteRef.current?.(); }, 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); runningRef.current = false; };
  }, [active]);

  return (
    <AnimatePresence>
      {phase > 0 && (
        <motion.div className="fixed inset-0 z-[9999]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>

          {/* ═══ PHASE 1: VIOLENT GLITCH ═══ */}
          {phase === 1 && (
            <div className="absolute inset-0" style={{ background: '#000' }}>
              {/* Heavy glitch slices */}
              {[...Array(20)].map((_, i) => {
                const h = 2 + Math.random() * 8;
                const y = Math.random() * 100;
                const xShift = (Math.random() - 0.5) * 120;
                const colors = ['rgba(57,255,20,0.3)', 'rgba(168,85,247,0.25)', 'rgba(255,0,60,0.2)', 'rgba(0,200,255,0.15)'];
                return (
                  <motion.div key={i} className="absolute left-0 right-0"
                    style={{ top: `${y}%`, height: `${h}%`, background: colors[i % 4], transform: `translateX(${xShift}px)`, mixBlendMode: 'screen' }}
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: [0, 1, 0.6, 1, 0.3, 0], scaleX: [0, 1.5, 0.7, 1.2, 0.4, 0], x: [0, xShift * 2, -xShift, xShift * 0.5, 0] }}
                    transition={{ duration: 0.7, delay: Math.random() * 0.2, ease: 'easeOut' }} />
                );
              })}

              {/* RGB split scanlines */}
              {[...Array(40)].map((_, i) => (
                <motion.div key={`s-${i}`} className="absolute left-0 right-0 h-[1px]"
                  style={{ top: `${i * 2.5}%`, background: i % 3 === 0 ? 'rgba(255,0,0,0.15)' : i % 3 === 1 ? 'rgba(0,255,0,0.12)' : 'rgba(0,0,255,0.1)' }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: [0, 1, 0.3, 1, 0] }}
                  transition={{ duration: 0.6, delay: i * 0.01 }} />
              ))}

              {/* Double scan sweep */}
              <motion.div className="absolute left-0 right-0 h-[3px]"
                style={{ background: 'linear-gradient(90deg, transparent, #39ff14, #39ff14, transparent)', boxShadow: '0 0 30px rgba(57,255,20,0.8), 0 -5px 20px rgba(57,255,20,0.3), 0 5px 20px rgba(57,255,20,0.3)' }}
                initial={{ top: '-5%' }} animate={{ top: '105%' }}
                transition={{ duration: 0.6, ease: 'easeIn' }} />
              <motion.div className="absolute left-0 right-0 h-[2px]"
                style={{ background: 'linear-gradient(90deg, transparent, #a855f7, #a855f7, transparent)', boxShadow: '0 0 20px rgba(168,85,247,0.6)' }}
                initial={{ bottom: '-5%' }} animate={{ bottom: '105%' }}
                transition={{ duration: 0.5, ease: 'easeIn', delay: 0.15 }} />

              {/* Static burst */}
              <motion.div className="absolute inset-0"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }}
                animate={{ opacity: [0, 0.4, 0.1, 0.35, 0] }}
                transition={{ duration: 0.7 }} />

              {/* Corrupted text flickers */}
              <motion.div className="absolute inset-0 flex items-center justify-center"
                animate={{ opacity: [0, 0, 1, 0, 0.8, 0, 0.6, 0] }}
                transition={{ duration: 0.7, times: [0, 0.15, 0.2, 0.3, 0.4, 0.5, 0.6, 0.8] }}>
                <span className="text-xs tracking-[1em] uppercase font-bold"
                  style={{ fontFamily: "'Courier New', monospace", color: '#39ff14', textShadow: '0 0 15px rgba(57,255,20,1), 0 0 40px rgba(57,255,20,0.5), -2px 0 #ff0040, 2px 0 #00d4ff' }}>
                  BREACH DETECTED
                </span>
              </motion.div>
            </div>
          )}

          {/* ═══ PHASE 2: LOGO EMERGE FROM DARKNESS ═══ */}
          {phase >= 2 && phase < 4 && (
            <motion.div className="absolute inset-0 flex flex-col items-center justify-center"
              style={{ background: '#000' }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>

              {/* Radial glow behind logo */}
              <motion.div className="absolute w-[500px] h-[500px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.15), rgba(57,255,20,0.05) 40%, transparent 70%)' }}
                initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.5, 1.2], opacity: [0, 0.8, 1] }}
                transition={{ duration: 1, ease: 'easeOut' }} />

              {/* Pulsing rings */}
              {[0, 1, 2].map(i => (
                <motion.div key={i} className="absolute rounded-full"
                  style={{ width: `${180 + i * 80}px`, height: `${180 + i * 80}px`, border: `1px solid ${i % 2 === 0 ? 'rgba(168,85,247,0.2)' : 'rgba(57,255,20,0.15)'}` }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [0, 1.2, 1], opacity: [0, 0.6, 0.2] }}
                  transition={{ duration: 1.2, delay: 0.2 + i * 0.15, ease: 'easeOut' }} />
              ))}

              {/* Logo */}
              <motion.img src={LOGO_URL} alt="GFG" className="relative z-10 w-28 h-28"
                style={{ filter: 'drop-shadow(0 0 30px rgba(168,85,247,0.5)) drop-shadow(0 0 60px rgba(57,255,20,0.2))' }}
                initial={{ scale: 0, rotate: -180, opacity: 0 }}
                animate={{ scale: [0, 1.3, 1], rotate: [-180, 10, 0], opacity: [0, 1, 1] }}
                transition={{ duration: 0.8, ease: 'easeOut' }} />

              {/* Title text */}
              {phase >= 3 && (
                <motion.div className="relative z-10 mt-6 text-center"
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                  <p className="text-[10px] tracking-[0.6em] uppercase mb-2"
                    style={{ fontFamily: "'Courier New', monospace", color: 'rgba(57,255,20,0.5)' }}>
                    Entering Protocol
                  </p>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-wider uppercase"
                    style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif", color: 'rgba(168,85,247,0.8)', textShadow: '0 0 20px rgba(168,85,247,0.4), 0 0 40px rgba(168,85,247,0.2)' }}>
                    Sewer Preview
                  </h2>
                  <motion.div className="mx-auto mt-3 h-[1px] w-48"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(57,255,20,0.4), transparent)' }}
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6, delay: 0.2 }} />

                  {/* Loading dots */}
                  <div className="flex items-center justify-center gap-1.5 mt-4">
                    {[0, 1, 2].map(i => (
                      <motion.div key={i} className="w-1.5 h-1.5 rounded-full"
                        style={{ background: '#39ff14' }}
                        animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
                        transition={{ duration: 0.8, delay: i * 0.2, repeat: Infinity }} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Corner brackets */}
              {[['top-8 left-8', 'border-l border-t'], ['top-8 right-8', 'border-r border-t'], ['bottom-8 left-8', 'border-l border-b'], ['bottom-8 right-8', 'border-r border-b']].map(([pos, brd], i) => (
                <motion.div key={i} className={`absolute ${pos} w-8 h-8 ${brd}`}
                  style={{ borderColor: i % 2 === 0 ? 'rgba(168,85,247,0.2)' : 'rgba(57,255,20,0.15)' }}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.1 }} />
              ))}
            </motion.div>
          )}

          {/* ═══ PHASE 4: FLASH OUT ═══ */}
          {phase === 4 && (
            <>
              <motion.div className="absolute inset-0" style={{ background: '#000' }} />
              <motion.div className="absolute inset-0"
                style={{ background: 'radial-gradient(circle at center, rgba(57,255,20,0.4), rgba(168,85,247,0.2), transparent)' }}
                initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, scale: 3 }}
                transition={{ duration: 0.4, ease: 'easeOut' }} />
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
